import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Row, Col, Typography, Button, Divider, Spin, Table, App } from "antd";
import { PrinterOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useReactToPrint } from "react-to-print";
import { convertNumberToWords, formatToCompanyTimezone, numberFormatTax } from "../../../lib/functions";
import { salesRecordWithDetailsApi } from "../../../apiservice/sales";
import { getSundryDebitorBalanceApi } from "../../../apiservice/accounts";
import ReportDom from "../../../components/common/ReportDom";

const { Title } = Typography;

const PosQuickVoucher = () => {
  const { id } = useParams(); // /pos/voucher/:id
  const navigate = useNavigate();
  const authInfo = useSelector((s) => s.auth.auth_info);

  const isIndia = authInfo?.country?.toLowerCase() === "india";
  const isIndiaGst = authInfo?.gst_enabled;
  const taxLabel = isIndia ? "GST" : "VAT";
  const { message } = App.useApp();

  const [voucher, setVoucher] = useState(null);
  const [dueAmount, setDueAmount] = useState(0);
  const [loading, setLoading] = useState(true);

  const reportRef = useRef();
  const handlePrint = useReactToPrint({
    contentRef: reportRef,
    documentTitle: `POS_Voucher_${voucher?.sale_voucher_no || "Voucher"}`,
  });

  useEffect(() => {
    if (id) fetchVoucher();
  }, [id]);

  const fetchVoucher = async () => {
    try {
      setLoading(true);
      const res = await salesRecordWithDetailsApi({
        sale_id: id,
        from: "voucher",
      });

      if (res?.data?.length > 0) {
        const data = res.data[0];
        setVoucher(data);

        if (data.acc_id) {
          const balRes = await getSundryDebitorBalanceApi({
            customerId: data.acc_id,
          });
          setDueAmount(balRes?.data?.accounts?.[0]?.balance || 0);
        }
      } else {
        message.warning("No POS Voucher data found.");
      }
    } catch (err) {
      console.error("POS voucher load failed:", err);
      message.error("Failed to load voucher.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", paddingTop: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!voucher) {
    return (
      <div style={{ padding: 24 }}>
        <Title level={4}>No Voucher Found</Title>
        <Button onClick={() => navigate("/pos/pos-entry")}>Back</Button>
      </div>
    );
  }

  const details = voucher?.details || [];

  // -------------------- FINAL CORRECT TOTAL CALCULATION --------------------
  let computedSubtotal = 0;
  let computedTax = 0;
  let computedDiscount = 0;

  if (voucher.is_cal_type === "individual") {
    // item_total already includes tax & discount
    details.forEach((item) => {
      computedSubtotal += Number(item.item_total);
      computedTax += Number(item.item_tax);
      computedDiscount += Number(item.item_discount);
    });
  } else if (voucher.is_cal_type === "on_total") {
    computedSubtotal = Number(voucher.sub_total);
    computedTax = Number(voucher.total_tax);
    computedDiscount = Number(voucher.total_discount);
  }

  let computedGrandTotal = 0;

  if (voucher.is_cal_type === "individual") {
    computedGrandTotal = computedSubtotal; // no need to add tax/discount
  } else {
    computedGrandTotal = computedSubtotal + computedTax - computedDiscount;
  }

  const computedPaid =
    voucher.trans?.reduce((sum, t) => sum + Number(t.tran_amount), 0) || 0;

  const computedDue = computedGrandTotal - computedPaid;

  const grandTotal = Number(computedGrandTotal.toFixed(2));
  // -------------------------------------------------------------------------

  const columns = [
    {
      title: "Desc.",
      dataIndex: "item_name",
      render: (_, record) => (
        <>
          <div>#{record.item_name}</div>
          <div style={{ fontSize: 12, color: "#555" }}>
            {record.item_qty} X {record.unit_symbol} qty
          </div>
        </>
      ),
    },
    {
      title: "Total",
      dataIndex: "item_total",
      align: "right",
      render: (t) => Number(t).toFixed(2),
    },
  ];

  return (
    <div style={{ padding: 24, background: "#f5f5f5", minHeight: "100vh" }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/pos/pos-entry")}
          >
            Back
          </Button>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<PrinterOutlined />}
            onClick={handlePrint}
          >
            Print Voucher
          </Button>
        </Col>
      </Row>

      <div
        style={{
          background: "white",
          padding: 24,
          borderRadius: 8,
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        }}
      >
        <Title level={4} style={{ textAlign: "center" }}>
          INVOICE
        </Title>
        <Divider style={{ margin: "8px 0" }} />

        <Row gutter={24}>
          <Col span={12}>
            <p>
              <b>INV NO:</b> {voucher.sale_voucher_no}
            </p>
            <p>
              <b>Name:</b> {voucher.acc_name}
            </p>
            <p>
              <b>Contact No:</b> {voucher.contact_no}
            </p>
            <p>
              <b>Date:</b> {formatToCompanyTimezone(voucher.created_date).split(" ")[0]}
            </p>
            <p>
              <b>By:</b> {voucher.full_name}
            </p>
          </Col>
        </Row>

        <Divider />

        <Table
          columns={columns}
          dataSource={details}
          pagination={false}
          bordered
          size="small"
          rowKey={(r) => r.item_id}
        />

        <Divider />

        <table style={{ width: "100%" }}>
          <tbody>
            {isIndiaGst && <>
              {voucher.place_of_supply_type == 'intra' && <>
                <tr>
                  <td>
                    <b>CGST :</b>
                  </td>
                  <td align="right">
                    {numberFormatTax(voucher.cgst_amount)}
                  </td>
                </tr>

                <tr>
                  <td>
                    <b>SGST :</b>
                  </td>
                  <td align="right">
                    {numberFormatTax(voucher.sgst_amount)}
                  </td>
                </tr>
              </>}

              {voucher.place_of_supply_type == 'inter' &&
                <tr>
                  <td>
                    <b>IGST :</b>
                  </td>
                  <td align="right">
                    {numberFormatTax(voucher.igst_amount)}
                  </td>
                </tr>}

              {voucher.gst_calculated == 'no' &&
                <tr>
                  <td>
                    <b>{taxLabel} :</b>
                  </td>
                  <td align="right">
                    {numberFormatTax(voucher.total_tax)}
                  </td>
                </tr>}

            </>
            }


            <tr>
              <td>
                <b>Sub Total :</b>
              </td>
              <td align="right">
                {Number(Number(voucher.sub_total).toFixed(2))}
              </td>
            </tr>

            {!isIndiaGst && <>
              <tr>
                <td>
                  <b>Vat :</b>
                </td>
                <td align="right">
                  {Number(Number(computedTax).toFixed(2)).toFixed(2)}
                </td>
              </tr>
            </>}

            {voucher.is_cal_type === "on_total" && (
              <>
                <tr>
                  <td>
                    <b>Discount :</b>
                  </td>
                  <td align="right">
                    {Number(Number(computedDiscount).toFixed(2)).toFixed(2)}
                  </td>
                </tr>

                {Number(voucher.total_transport_cost) !== 0 && (
                  <tr>
                    <td>
                      <b>Service Charge :</b>
                    </td>
                    <td align="right">
                      {Number(voucher.total_transport_cost).toFixed(2)}
                    </td>
                  </tr>
                )}
              </>
            )}

            <tr>
              <td colSpan={2}>
                <hr />
              </td>
            </tr>

            <tr>
              <td>
                <b>Grand Total ({authInfo?.currency || "INR"}):</b>
              </td>
              <td align="right">{Number(voucher.total_amount).toFixed(2)}</td>
              {/* <td align="right">{grandTotal.toFixed(2)}</td> */}
            </tr>

            <tr>
              <td colSpan={2}>
                <hr />
              </td>
            </tr>

            <tr>
              <td>
                <b>Paid :</b> {authInfo?.currency || "INR"}
              </td>
              <td align="right">{Number(computedPaid).toFixed(2)}</td>
            </tr>

            <tr>
              <td>
                <b>Due :</b> {authInfo?.currency || "INR"}
              </td>
              <td align="right">{Number(dueAmount).toFixed(2)}</td>
            </tr>
          </tbody>
        </table>

        <Divider />

        <p>
          <b>In Word of Grand Total:</b>{" "}
          {convertNumberToWords(grandTotal, authInfo?.currency || "AED")}
        </p>

        <p>
          <b>Description:</b> {voucher.narration || ""}
        </p>
      </div>

      {/* Hidden print layout */}
      <div style={{ position: "absolute", top: "-9999px", left: "-9999px" }}>
        <ReportDom
          ref={reportRef}
          title="INVOICE"
          filters={{
            fromDate: voucher.created_date,
            toDate: voucher.created_date,
          }}
          renderTable={() => (
            <Table
              columns={columns}
              dataSource={details}
              pagination={false}
              bordered
              size="small"
              rowKey={(r) => r.item_id}
            />
          )}
          extraSections={[
            {
              title: "Summary",
              columns: [
                { title: "Sub Total", dataIndex: "subTotal" },
                { title: taxLabel, dataIndex: "vat" },
                { title: "Discount", dataIndex: "discount" },
                { title: "Grand Total", dataIndex: "grandTotal" },
                { title: "Paid", dataIndex: "paid" },
                { title: "Due", dataIndex: "due" },
              ],
              data: [
                {
                  subTotal: computedSubtotal.toFixed(2),
                  vat: computedTax.toFixed(2),
                  discount: computedDiscount.toFixed(2),
                  grandTotal: grandTotal,
                  paid: computedPaid.toFixed(2),
                  due: computedDue.toFixed(2),
                },
              ],
            },
            {
              title: "Description",
              columns: [
                { title: "In Words", dataIndex: "inWords" },
                { title: "Narration", dataIndex: "narration" },
              ],
              data: [
                {
                  inWords: convertNumberToWords(
                    grandTotal,
                    authInfo?.currency || "AED"
                  ),
                  narration: voucher.narration || "",
                },
              ],
            },
          ]}
        />
      </div>
    </div>
  );
};

export default PosQuickVoucher;
